/**
 * Zustand Store Wrappers - ADR-001 Enforcement
 *
 * These hooks wrap useGlobalState to enforce shallow equality checking
 * as required by ADR-001: Zustand for Client-Side State Management.
 *
 * @see docs/adr/0001-zustand-state-management.md
 * @see docs/adr/plans/ADR-001-ENFORCEMENT-REPORT.md
 */

import { useGlobalState } from '../state/useGlobalState';
import { useShallow } from 'zustand/react/shallow';
import type { StoreState } from '../types/types';

/**
 * Primary hook for selecting state from the global store.
 *
 * **USE THIS** when selecting objects or arrays to prevent unnecessary re-renders.
 * Uses shallow equality comparison by default as required by ADR-001.
 *
 * @example
 * // ✅ CORRECT - Selecting an object
 * const actions = useStore(state => state.actions);
 *
 * @example
 * // ✅ CORRECT - Selecting multiple values into an object
 * const { npcs, monsters } = useStore(state => ({
 *   npcs: state.session.roster.npcs,
 *   monsters: state.session.roster.monsters
 * }));
 *
 * @example
 * // ✅ CORRECT - Selecting an array
 * const soundscapes = useStore(state => state.session.cache.soundscapes);
 *
 * @param selector Function that selects data from the store
 * @returns The selected data with shallow equality checking
 */
export function useStore<T>(
  selector: (state: StoreState) => T
): T {
  return useGlobalState(useShallow(selector));
}

/**
 * Hook for selecting primitive values from the global store.
 *
 * **USE THIS** when selecting primitive values (string, number, boolean, null, undefined)
 * where shallow equality is not needed.
 *
 * This hook exists for:
 * 1. Self-documentation (makes it clear you're selecting a primitive)
 * 2. Type safety (TypeScript will error if you select an object/array)
 *
 * @example
 * // ✅ CORRECT - Selecting primitive values
 * const campaignId = useStorePrimitive(state => state.session.general.activeCampaignId);
 * const isActive = useStorePrimitive(state => state.session.combat.isActive);
 * const round = useStorePrimitive(state => state.session.combat.round);
 *
 * @example
 * // ❌ WRONG - Don't use for objects/arrays
 * const actions = useStorePrimitive(state => state.actions); // TypeScript error!
 *
 * @param selector Function that selects a primitive value from the store
 * @returns The selected primitive value
 */
export function useStorePrimitive<T extends string | number | boolean | null | undefined>(
  selector: (state: StoreState) => T
): T {
  return useGlobalState(selector);
}

/**
 * Hook for selecting the actions object from the global store.
 *
 * This is a convenience wrapper for the most common use case.
 * Uses shallow equality by default as required by ADR-001.
 *
 * @example
 * // ✅ CORRECT - Use this instead of selecting actions manually
 * const actions = useActions();
 * actions.addPlayer({ name: 'Aragorn', class: 'Ranger' });
 *
 * @example
 * // ✅ ALSO CORRECT - Destructure specific actions
 * const { addPlayer, deletePlayer } = useActions();
 *
 * @returns The actions object with all mutation functions
 */
export function useActions() {
  return useStore(state => state.actions);
}

/**
 * DEPRECATED: Direct use of useGlobalState is discouraged.
 *
 * **Migration Guide:**
 * - For objects/arrays: use `useStore()`
 * - For primitives: use `useStorePrimitive()`
 * - For actions: use `useActions()`
 *
 * @deprecated Use useStore, useStorePrimitive, or useActions instead
 * @see useStore
 * @see useStorePrimitive
 * @see useActions
 */
